import { LocaleCode } from './i18n.types';
import { TranslationKey, translateForLocale } from './i18n.translations';
import { formatCurrencyAmount, normalizeCurrencyCode } from './currency-format';

const US_PRICE_FORMATTER = new Intl.NumberFormat('en-US');

function t(locale: LocaleCode, key: TranslationKey, params?: Record<string, string | number | null>): string {
  return translateForLocale(locale, key, params);
}

export function formatPriceValue(value: number | null | undefined): string {
  const normalized = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : 0;
  return US_PRICE_FORMATTER.format(normalized);
}

export function formatAdId(value: number | null | undefined): string {
  const normalized = typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, Math.trunc(value))
    : 0;
  return String(normalized).padStart(8, '0');
}

export function getCategoryLabel(
  locale: LocaleCode,
  type: number,
  variant: 'single' | 'plural' = 'single'
): string {
  if (variant === 'plural') {
    switch (type) {
      case 1:
        return t(locale, 'category.apartments.plural');
      case 2:
        return t(locale, 'category.houses.plural');
      case 3:
        return t(locale, 'category.commercial.plural');
      case 4:
        return t(locale, 'category.land.plural');
      default:
        return t(locale, 'category.object');
    }
  }

  switch (type) {
    case 1:
      return t(locale, 'category.apartment');
    case 2:
      return t(locale, 'category.house');
    case 3:
      return t(locale, 'category.commercial');
    case 4:
      return t(locale, 'category.land');
    default:
      return t(locale, 'category.object');
  }
}

export function getDealTypeLabel(locale: LocaleCode, dealType: number): string {
  return dealType === 1 ? t(locale, 'deal.rent') : t(locale, 'deal.sale');
}

export function getCurrencyLabel(locale: LocaleCode, dealType: number, rentType: number | null): string {
  if (dealType !== 1) {
    return t(locale, 'common.currency.sale');
  }

  return rentType === 2
    ? t(locale, 'common.currency.rentMonthly')
    : t(locale, 'common.currency.rentDaily');
}

export function getPriceSuffix(locale: LocaleCode, dealType: number, rentType: number | null): string {
  if (dealType !== 1) {
    return t(locale, 'common.priceBase');
  }

  return rentType === 2 ? t(locale, 'common.pricePerMonth') : t(locale, 'common.pricePerDay');
}

export interface AdPriceDisplay {
  price?: number | null;
  currency?: string | null;
  original_price?: number | null;
  original_currency?: string | null;
  display_price?: number | null;
  display_currency?: string | null;
  deal_type: number;
  rent_type?: number | null;
}

export function getDisplayPriceValue(ad: Pick<AdPriceDisplay, 'price' | 'original_price' | 'display_price'>): number | null {
  return ad.display_price ?? ad.original_price ?? ad.price ?? null;
}

export function getDisplayCurrency(ad: Pick<AdPriceDisplay, 'currency' | 'original_currency' | 'display_currency'>): string {
  return normalizeCurrencyCode(ad.display_currency ?? ad.original_currency ?? ad.currency);
}

export function getRentPeriodSuffix(locale: LocaleCode, dealType: number, rentType: number | null): string {
  if (dealType !== 1) {
    return '';
  }

  return rentType === 2 ? t(locale, 'common.period.month') : t(locale, 'common.period.day');
}

export function formatAdPriceAmount(ad: AdPriceDisplay, compact = false): string {
  return formatCurrencyAmount(getDisplayPriceValue(ad), getDisplayCurrency(ad), { compact });
}

export function formatAdPriceLabel(locale: LocaleCode, ad: AdPriceDisplay, compact = false): string {
  return `${formatAdPriceAmount(ad, compact)}${getRentPeriodSuffix(locale, ad.deal_type, ad.rent_type ?? null)}`.trim();
}

export function getListingFallbackTitle(locale: LocaleCode, id?: number | null): string {
  if (id) {
    return t(locale, 'common.listingNumber', { id });
  }

  return t(locale, 'common.listingFallback');
}

export function formatFloorLabel(
  locale: LocaleCode,
  floor: number | null | undefined,
  totalFloors: number | null | undefined
): string {
  if (!floor) {
    return '';
  }

  if (totalFloors) {
    return t(locale, 'common.floorRange', { floor, total: totalFloors });
  }

  return t(locale, 'common.floorSingle', { floor });
}
